import { useBlocker } from "@tanstack/react-router";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useId, useRef } from "react";

type UnsavedNavigation = {
  confirmLeave: () => boolean;
  allowUnload: () => void;
};

type UnsavedChangesContext = UnsavedNavigation & {
  report: (id: string, dirty: boolean) => void;
};

const UnsavedContext = createContext<UnsavedChangesContext>({ report: () => undefined, confirmLeave: () => true, allowUnload: () => undefined });

export function useUnsavedChanges(dirty: boolean): () => void {
  const { report } = useContext(UnsavedContext);
  const id = useId();
  useEffect(() => {
    report(id, dirty);
    return () => report(id, false);
  }, [dirty, id, report]);
  return useCallback(() => report(id, false), [id, report]);
}

export function useUnsavedChangesNavigation(): UnsavedNavigation {
  return useContext(UnsavedContext);
}

export function UnsavedChanges({ children }: { children: ReactNode }): ReactNode {
  const dirtyIds = useRef<ReadonlySet<string>>(new Set());
  const unloadAllowed = useRef<boolean>(false);
  const report = useCallback((id: string, dirty: boolean): void => {
    if (dirtyIds.current.has(id) === dirty) return;
    const next = new Set(dirtyIds.current);
    if (dirty) next.add(id);
    else next.delete(id);
    // A successful mutation may navigate before React commits the next render.
    // Clear only its own guard immediately; other forms must still block navigation.
    dirtyIds.current = next;
  }, []);
  const confirmLeave = useCallback((): boolean => dirtyIds.current.size === 0 || window.confirm("Leave this page? Unsaved changes will be lost. Any pending work may still finish."), []);
  const allowUnload = useCallback((): void => {
    // Used only immediately before a confirmed full-page navigation, after the logout attempt finishes.
    unloadAllowed.current = true;
  }, []);
  useBlocker({
    shouldBlockFn: () => !confirmLeave(),
    enableBeforeUnload: () => dirtyIds.current.size > 0 && !unloadAllowed.current,
  });
  return <UnsavedContext.Provider value={{ report, confirmLeave, allowUnload }}>{children}</UnsavedContext.Provider>;
}
